import type { DefaultRootState } from 'react-redux';

import { CampaignCreationWizardStep } from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CampaignProductType } from 'utils/constants/campaign';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import type { AuctionCampaign } from 'store/models/auctionData';
import { AppThunkAction } from 'store/utils';

export enum WizardCpcMode {
    Quick = 'quick',
    Flexible = 'flexible',
}

// ----------------------- actions -------------------------------------
const SET_CAMPAIGN_CREATION_WIZARD_ACTIVE_STEP = 'SET_CAMPAIGN_CREATION_WIZARD_ACTIVE_STEP';
const RESET_CAMPAIGN_CREATION_WIZARD = 'RESET_CAMPAIGN_CREATION_WIZARD';
const SET_WIZARD_CAMPAIGN_TARGET = 'SET_WIZARD_CAMPAIGN_TARGET';
const SET_WIZARD_VACANCIES = 'SET_WIZARD_VACANCIES';
const SET_WIZARD_VACANCY_FILTERS = 'SET_WIZARD_VACANCY_FILTERS';
const SET_WIZARD_SELECTED_VACANCY = 'SET_WIZARD_SELECTED_VACANCY';
const SET_WIZARD_VACANCY_SEARCH_VALUE = 'SET_WIZARD_VACANCY_SEARCH_VALUE';
const SET_WIZARD_SELECTED_FILTER_VALUES = 'SET_WIZARD_SELECTED_FILTER_VALUES';
const SET_WIZARD_WEBSITE_URL = 'SET_WIZARD_WEBSITE_URL';
const SET_WIZARD_VACANCIES_IS_LOADING = 'SET_WIZARD_VACANCIES_IS_LOADING';
const SET_WIZARD_CAMPAIGN_OPTIONS = 'SET_WIZARD_CAMPAIGN_OPTIONS';
const SET_WIZARD_CAMPAIGN_OPTIONS_IS_LOADING = 'SET_WIZARD_CAMPAIGN_OPTIONS_IS_LOADING';
const SET_WIZARD_SELECTED_PRODUCT_TYPE = 'SET_WIZARD_SELECTED_PRODUCT_TYPE';
const SET_WIZARD_WEBSITE_URL_ERROR_KEY = 'SET_WIZARD_WEBSITE_URL_ERROR_KEY';
const SET_WIZARD_CPC_MODE = 'SET_WIZARD_CPC_MODE';

/** Индекс текущего шага визарда, начиная с 0. Шаги динамические и зависят от флоу, поэтому фиксированных констант нет. */
type ActiveStep = number;

export const enum CampaignTarget {
    Vacancy = 'vacancy',
    Website = 'website',
}

export interface WizardVacancy {
    vacancyId: number;
    name: string;
    areaName: string;
    managerName: string;
    foreignArea: boolean;
}

export interface WizardFilterOption {
    value: string;
    text: string;
}

export interface WizardVacancyFilters {
    areas: WizardFilterOption[];
    users: WizardFilterOption[];
}

export interface CpcProductData {
    budget: {
        minValue: number;
    };
}

export interface AuctionProductData {
    budget: {
        minValue: number;
    };
    auctionCampaign: AuctionCampaign | null;
}

export interface WizardCampaignOptions {
    productTypes: CampaignProductType[];
    auctionProductData: AuctionProductData | null;
    cpcProductData: CpcProductData | null;
}

interface SelectedFilterValues {
    areas: WizardFilterOption[];
    users: WizardFilterOption[];
}

interface PayloadTypes {
    [SET_CAMPAIGN_CREATION_WIZARD_ACTIVE_STEP]: ActiveStep;
    [RESET_CAMPAIGN_CREATION_WIZARD]: null;
    [SET_WIZARD_CAMPAIGN_TARGET]: CampaignTarget;
    [SET_WIZARD_VACANCIES]: WizardVacancy[];
    [SET_WIZARD_VACANCY_FILTERS]: WizardVacancyFilters;
    [SET_WIZARD_SELECTED_VACANCY]: { vacancyId: number; vacancyName?: string; foreignArea?: boolean } | null;
    [SET_WIZARD_VACANCY_SEARCH_VALUE]: string;
    [SET_WIZARD_SELECTED_FILTER_VALUES]: SelectedFilterValues;
    [SET_WIZARD_WEBSITE_URL]: string;
    [SET_WIZARD_VACANCIES_IS_LOADING]: boolean;
    [SET_WIZARD_CAMPAIGN_OPTIONS]: {
        vacancyId: number;
        campaignOptions: WizardCampaignOptions;
    };
    [SET_WIZARD_CAMPAIGN_OPTIONS_IS_LOADING]: boolean;
    [SET_WIZARD_SELECTED_PRODUCT_TYPE]: CampaignProductType | null;
    [SET_WIZARD_WEBSITE_URL_ERROR_KEY]: string | null;
    [SET_WIZARD_CPC_MODE]: WizardCpcMode | null;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setCampaignCreationWizardActiveStep = ActionCreator(SET_CAMPAIGN_CREATION_WIZARD_ACTIVE_STEP);
export const resetCampaignCreationWizard = ActionCreator(RESET_CAMPAIGN_CREATION_WIZARD);
export const setWizardCampaignTarget = ActionCreator(SET_WIZARD_CAMPAIGN_TARGET);
export const setWizardVacancies = ActionCreator(SET_WIZARD_VACANCIES);
export const setWizardVacancyFilters = ActionCreator(SET_WIZARD_VACANCY_FILTERS);
export const setWizardSelectedVacancy = ActionCreator(SET_WIZARD_SELECTED_VACANCY);
export const setWizardVacancySearchValue = ActionCreator(SET_WIZARD_VACANCY_SEARCH_VALUE);
export const setWizardSelectedFilterValues = ActionCreator(SET_WIZARD_SELECTED_FILTER_VALUES);
export const setWizardWebsiteUrl = ActionCreator(SET_WIZARD_WEBSITE_URL);
export const setWizardVacanciesIsLoading = ActionCreator(SET_WIZARD_VACANCIES_IS_LOADING);
export const setWizardCampaignOptions = ActionCreator(SET_WIZARD_CAMPAIGN_OPTIONS);
export const setWizardCampaignOptionsIsLoading = ActionCreator(SET_WIZARD_CAMPAIGN_OPTIONS_IS_LOADING);
export const setWizardSelectedProductType = ActionCreator(SET_WIZARD_SELECTED_PRODUCT_TYPE);
export const setWizardWebsiteUrlErrorKey = ActionCreator(SET_WIZARD_WEBSITE_URL_ERROR_KEY);
export const setWizardCpcMode = ActionCreator(SET_WIZARD_CPC_MODE);
export const goBackToPromotionObjectStep = (): AppThunkAction<void> => (dispatch) => {
    dispatch(setCampaignCreationWizardActiveStep(CampaignCreationWizardStep.PromotionObject));
    dispatch(setWizardSelectedProductType(null));
    dispatch(setWizardCpcMode(null));
};

export const goToPromotionTypeStep =
    (vacancy: { vacancyId: number; vacancyName?: string; foreignArea?: boolean }): AppThunkAction<void> =>
    (dispatch) => {
        dispatch(setWizardSelectedVacancy(vacancy));
        dispatch(setWizardSelectedProductType(null));
        dispatch(setWizardCpcMode(null));
        dispatch(setCampaignCreationWizardActiveStep(CampaignCreationWizardStep.PromotionType));
    };
// ----------------------- /actions ------------------------------------

// ----------------------- reducer -------------------------------------
interface CampaignCreationWizardState {
    activeStep: ActiveStep;
    campaignTarget: CampaignTarget;
    vacancies: WizardVacancy[];
    vacancyFilters: WizardVacancyFilters;
    selectedVacancyId: number | null;
    selectedVacancyName: string | null;
    selectedVacancyForeignArea: boolean;
    vacancySearchValue: string;
    selectedFilterValues: SelectedFilterValues;
    websiteUrl: string;
    vacanciesIsLoading: boolean;
    campaignOptions: WizardCampaignOptions | null;
    campaignOptionsVacancyId: number | null;
    campaignOptionsIsLoading: boolean;
    selectedProductType: CampaignProductType | null;
    websiteUrlErrorKey: string | null;
    selectedCpcMode: WizardCpcMode | null;
}

const CAMPAIGN_CREATION_WIZARD_INITIAL_STATE: CampaignCreationWizardState = {
    activeStep: CampaignCreationWizardStep.PromotionObject,
    campaignTarget: CampaignTarget.Vacancy,
    vacancies: [],
    vacancyFilters: { areas: [], users: [] },
    selectedVacancyId: null,
    selectedVacancyName: null,
    selectedVacancyForeignArea: false,
    vacancySearchValue: '',
    selectedFilterValues: { areas: [], users: [] },
    websiteUrl: '',
    vacanciesIsLoading: false,
    campaignOptions: null,
    campaignOptionsVacancyId: null,
    campaignOptionsIsLoading: false,
    selectedProductType: null,
    websiteUrlErrorKey: null,
    selectedCpcMode: null,
};

export default createReducer<CampaignCreationWizardState, PayloadTypes>(CAMPAIGN_CREATION_WIZARD_INITIAL_STATE, {
    [SET_CAMPAIGN_CREATION_WIZARD_ACTIVE_STEP]: (state, { payload }) => ({ ...state, activeStep: payload }),
    [RESET_CAMPAIGN_CREATION_WIZARD]: () => CAMPAIGN_CREATION_WIZARD_INITIAL_STATE,
    [SET_WIZARD_CAMPAIGN_TARGET]: (state, { payload }) => ({ ...state, campaignTarget: payload }),
    [SET_WIZARD_VACANCIES]: (state, { payload }) => ({ ...state, vacancies: payload }),
    [SET_WIZARD_VACANCY_FILTERS]: (state, { payload }) => ({ ...state, vacancyFilters: payload }),
    [SET_WIZARD_SELECTED_VACANCY]: (state, { payload }) => {
        const selectedVacancyId = payload?.vacancyId ?? null;
        const isSelectedVacancyChanged = state.selectedVacancyId !== selectedVacancyId;
        return {
            ...state,
            selectedVacancyId,
            selectedVacancyName: payload?.vacancyName ?? null,
            selectedVacancyForeignArea: payload?.foreignArea ?? false,
            ...(isSelectedVacancyChanged && {
                campaignOptions: null,
                campaignOptionsVacancyId: null,
            }),
        };
    },
    [SET_WIZARD_VACANCY_SEARCH_VALUE]: (state, { payload }) => ({ ...state, vacancySearchValue: payload }),
    [SET_WIZARD_SELECTED_FILTER_VALUES]: (state, { payload }) => ({ ...state, selectedFilterValues: payload }),
    [SET_WIZARD_WEBSITE_URL]: (state, { payload }) => ({
        ...state,
        websiteUrl: payload,
        websiteUrlErrorKey: null,
    }),
    [SET_WIZARD_VACANCIES_IS_LOADING]: (state, { payload }) => ({ ...state, vacanciesIsLoading: payload }),
    [SET_WIZARD_CAMPAIGN_OPTIONS]: (state, { payload }) => ({
        ...state,
        campaignOptions: payload.campaignOptions,
        campaignOptionsVacancyId: payload.vacancyId,
    }),
    [SET_WIZARD_CAMPAIGN_OPTIONS_IS_LOADING]: (state, { payload }) => ({
        ...state,
        campaignOptionsIsLoading: payload,
    }),
    [SET_WIZARD_SELECTED_PRODUCT_TYPE]: (state, { payload }) => ({ ...state, selectedProductType: payload }),
    [SET_WIZARD_WEBSITE_URL_ERROR_KEY]: (state, { payload }) => ({ ...state, websiteUrlErrorKey: payload }),
    [SET_WIZARD_CPC_MODE]: (state, { payload }) => ({ ...state, selectedCpcMode: payload }),
});
// ----------------------- /reducer ------------------------------------

// ----------------------- selectors -----------------------------------
export const selectCampaignCreationWizardActiveStep = (state: DefaultRootState): ActiveStep =>
    state.campaignCreationWizard.activeStep;
export const selectWizardCampaignTarget = (state: DefaultRootState): CampaignTarget =>
    state.campaignCreationWizard.campaignTarget;
export const selectWizardVacancies = (state: DefaultRootState): WizardVacancy[] =>
    state.campaignCreationWizard.vacancies;
export const selectWizardVacancyFilters = (state: DefaultRootState): WizardVacancyFilters =>
    state.campaignCreationWizard.vacancyFilters;
export const selectWizardSelectedVacancyId = (state: DefaultRootState): number | null =>
    state.campaignCreationWizard.selectedVacancyId;
export const selectWizardVacancySearchValue = (state: DefaultRootState): string =>
    state.campaignCreationWizard.vacancySearchValue;
export const selectWizardSelectedFilterValues = (state: DefaultRootState): SelectedFilterValues =>
    state.campaignCreationWizard.selectedFilterValues;
export const selectWizardWebsiteUrl = (state: DefaultRootState): string => state.campaignCreationWizard.websiteUrl;
export const selectWizardVacanciesIsLoading = (state: DefaultRootState): boolean =>
    state.campaignCreationWizard.vacanciesIsLoading;
export const selectWizardCampaignOptions = (state: DefaultRootState): WizardCampaignOptions | null =>
    state.campaignCreationWizard.campaignOptionsVacancyId === state.campaignCreationWizard.selectedVacancyId
        ? state.campaignCreationWizard.campaignOptions
        : null;
export const selectWizardCampaignOptionsIsLoading = (state: DefaultRootState): boolean =>
    state.campaignCreationWizard.campaignOptionsIsLoading;
export const selectWizardSelectedProductType = (state: DefaultRootState): CampaignProductType | null =>
    state.campaignCreationWizard.selectedProductType;
export const selectWizardSelectedVacancyName = (state: DefaultRootState): string | null =>
    state.campaignCreationWizard.selectedVacancyName;
export const selectWizardSelectedVacancyForeignArea = (state: DefaultRootState): boolean =>
    state.campaignCreationWizard.selectedVacancyForeignArea;
export const selectWizardWebsiteUrlErrorKey = (state: DefaultRootState): string | null =>
    state.campaignCreationWizard.websiteUrlErrorKey;
export const selectWizardCpcMode = (state: DefaultRootState): WizardCpcMode | null =>
    state.campaignCreationWizard.selectedCpcMode;
export const selectIsPromotionObjectStepValid = (state: DefaultRootState): boolean => {
    const { campaignTarget, selectedVacancyId, websiteUrl } = state.campaignCreationWizard;
    if (campaignTarget === CampaignTarget.Vacancy) {
        return selectedVacancyId !== null;
    }
    return websiteUrl.trim() !== '';
};
// ----------------------- /selectors ----------------------------------
